import { type MCPServerInfo } from '../../types';
import { toUrlPathSegment } from '../../../strings';

// Okta runs as a Natoma-hosted MCP K8s server; the admin-provided Okta org
// domain is a path segment that routes the request to the right tenant:
//   http://okta-natoma-mcp.mcp-servers.svc.cluster.local/{oktaDomain}/mcp
const MCP_HOST = 'http://okta-natoma-mcp.mcp-servers.svc.cluster.local';

// oktaDomain is admin-provided and flows downstream — okta-mcp-server builds the
// OAuth authorization server and Okta Management API calls from it — so an
// arbitrary host would be an SSRF / auth-redirect vector. Reject anything that
// isn't an Okta-owned hostname. Mirror of okta-mcp-server's src/oktaDomain.ts
// (ALLOWED_OKTA_SUFFIXES); keep the two in sync.
const ALLOWED_OKTA_SUFFIXES = [
  '.okta.com',
  '.okta-emea.com',
  '.oktapreview.com'
];
const OKTA_HOSTNAME_REGEX =
  /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]*[a-z0-9])?)+$/i;

function assertValidOktaDomain(domain: string | undefined): string {
  const value = domain?.trim();
  if (!value) {
    throw new Error(
      'Okta organization domain is required (e.g. your-org.okta.com).'
    );
  }
  const lower = value.toLowerCase();
  const valid =
    value.length <= 253 &&
    OKTA_HOSTNAME_REGEX.test(value) &&
    ALLOWED_OKTA_SUFFIXES.some((suffix) => lower.endsWith(suffix));
  if (!valid) {
    throw new Error(
      `Invalid Okta organization domain: "${value}". Must be an Okta-owned host (e.g. your-org.okta.com).`
    );
  }
  return value;
}

const OktaServer: MCPServerInfo = {
  serverId: 'OKTA_NATOMA',
  name: 'Okta (Natoma)',
  slug: 'okta-natoma',
  description:
    'Okta MCP Server — manage users, groups, applications, policies, and system logs via user-consent OAuth',
  source: {
    sourceType: 'REMOTE_URL',
    transport: 'STREAMABLE_HTTP',
    mcpServerUrl: (config: Map<string, string>): string => {
      // Validate it's an Okta-owned host, then encode as a single path segment.
      const oktaDomain = assertValidOktaDomain(config.get('oktaDomain'));
      return `${MCP_HOST}/${toUrlPathSegment(oktaDomain, {
        label: 'Okta organization domain'
      })}/mcp`;
    }
  },
  authenticationType: 'OAUTH',
  serverConfig: {
    required: ['oktaDomain'],
    properties: {
      oktaDomain: {
        type: 'string',
        description:
          'Okta organization domain (e.g. your-org.okta.com, your-org.okta-emea.com, your-org.oktapreview.com)',
        sensitive: false,
        isUrlParameter: true
      }
    }
  },
  installConfig: {
    required: [],
    properties: {}
  },
  annotations: {
    hintPreRegistered: true
  }
};

export default OktaServer;
